import apiClient from "./client";
import type { ApiCategory } from "./transactions";

export async function getCategories(): Promise<ApiCategory[]> {
  const { data } = await apiClient.get<ApiCategory[]>("/categories/");
  return data;
}

export interface CreateCategoryData {
  name: string;
  parent_category?: string | null;
  icon?: string | null;
}

export async function createCategory(data: CreateCategoryData): Promise<ApiCategory> {
  const { data: result } = await apiClient.post<ApiCategory>("/categories/", data);
  return result;
}

export interface ApiCategoryRule {
  id: number;
  merchant_pattern: string;
  // The list/detail response NESTS the full category object (read serializer);
  // only the create *request* takes a numeric id (see CreateCategoryRuleData).
  category: ApiCategory;
  // Parent bucket enum on the rule model (needs/wants/savings) — required.
  parent_category: string;
  apply_retroactively: boolean;
  /** Contract batch 15 (SC17): blank, or the one P2P recipient this rule files
   *  (normalised). Optional: older payloads have none. */
  counterparty?: string;
  created_at: string;
}

export async function getCategoryRules(): Promise<ApiCategoryRule[]> {
  const { data } = await apiClient.get<ApiCategoryRule[]>("/category-rules/");
  return data;
}

export interface CreateCategoryRuleData {
  merchant_pattern: string;
  category: number;
  // Required by the backend — the parent bucket (needs/wants/savings) of the
  // chosen category.
  parent_category: string;
  apply_retroactively?: boolean;
}

/**
 * What `POST /category-rules/` actually returns (#1056).
 *
 * `CategoryRuleListCreateView.get_serializer_class` swaps in
 * `CategoryRuleCreateSerializer` for POST, and the response carries its four
 * WRITE fields plus `recategorized_count` (CAT-006) — no `id`, no `created_at`, and `category` as the primary key
 * rather than the nested object. This function used to be typed as returning
 * `ApiCategoryRule`, the read shape, so `result.id` and `result.created_at`
 * were `undefined` and `result.category` was a number wearing an
 * `ApiCategory`'s type. Nothing read them, which is why it survived: `Rules.tsx`
 * discards the result and invalidates the list. `rule_created` was latent in
 * exactly the same way until #860 gave it a caller.
 *
 * Same shape mobile declares in `mobile/src/api/categories.ts`. A caller that
 * needs the id refetches the list, or the create view is changed to return
 * `CategoryRuleSerializer` — a backend and schema change with its own decision
 * to make, noted on #1056.
 */
export interface CreatedCategoryRule {
  merchant_pattern: string;
  category: number;
  parent_category: string;
  apply_retroactively: boolean;
  /** Past transactions the rule recategorised; null when not retroactive (CAT-006). */
  recategorized_count: number | null;
}

export async function createCategoryRule(
  data: CreateCategoryRuleData,
): Promise<CreatedCategoryRule> {
  const { data: result } = await apiClient.post<CreatedCategoryRule>("/category-rules/", data);
  return result;
}

/**
 * Fields a rule edit may change. All optional: the endpoint is PATCH only
 * (`CategoryRuleDetailView` removes PUT), so a partial body is the contract
 * rather than a convenience.
 *
 * `parent_category` travels with `category` because the backend stores the
 * bucket on the rule itself, not through the category. Sending a new category
 * without its bucket leaves the rule pointing at, say, a Wants category while
 * still counting as Needs.
 */
export interface UpdateCategoryRuleData {
  merchant_pattern?: string;
  category?: number;
  parent_category?: string;
  apply_retroactively?: boolean;
}

/**
 * What `PATCH /category-rules/{id}/` returns.
 *
 * `CategoryRuleDetailView.get_serializer_class` swaps in
 * `CategoryRuleUpdateSerializer` for PATCH, and that serializer declares the
 * same four write fields as the create serializer: no `id`, no `created_at`,
 * and `category` as the primary key rather than the nested object. Same trap as
 * #1056; the alias keeps the two shapes tied together so a field added to one
 * serializer cannot silently diverge here.
 */
export type UpdatedCategoryRule = CreatedCategoryRule;

export async function updateCategoryRule(
  id: number,
  data: UpdateCategoryRuleData,
): Promise<UpdatedCategoryRule> {
  const { data: result } = await apiClient.patch<UpdatedCategoryRule>(
    `/category-rules/${id}/`,
    data,
  );
  return result;
}

export async function deleteCategoryRule(id: number): Promise<void> {
  await apiClient.delete(`/category-rules/${id}/`);
}
